"use client";

import { useTranslations } from "next-intl";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

// Module-level flag survives React remounts (e.g. WagmiProvider key change)
// but resets on full page reload, so splash only plays once per load.
let hasShownSplash = false;

export const SplashScreen = () => {
  const tc = useTranslations("common");
  const [phase, setPhase] = useState<"enter" | "exit" | "done">("enter");

  // useLayoutEffect fires before paint — prevents content flash on remount.
  // If splash already shown, jump straight to "done" before first paint.
  useLayoutEffect(() => {
    if (hasShownSplash) {
      setPhase("done");
    } else {
      hasShownSplash = true;
    }
  }, []);

  useEffect(() => {
    if (phase !== "enter") return;
    const timer = setTimeout(() => {
      setPhase("exit");
    }, 1800);
    return () => clearTimeout(timer);
  }, [phase]);

  const handleAnimationEnd = useCallback(() => {
    if (phase === "exit") {
      setPhase("done");
    }
  }, [phase]);

  if (phase === "done") return null;

  return (
    <div
      suppressHydrationWarning
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-background"
      style={
        phase === "exit"
          ? { animation: "splash-exit 0.4s ease-in-out forwards" }
          : undefined
      }
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        className="flex flex-col items-center gap-4"
        style={{ animation: "splash-enter 0.5s ease-out both" }}
      >
        {/* Inline SVG – no network request, renders instantly */}
        <svg
          width="56"
          height="56"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="logo"
          className="text-foreground"
        >
          <path
            d="M11.1141 0.179005C9.18512 0.559759 8.00223 1.70203 7.85664 3.3157C7.69286 5.292 9.09413 6.61558 11.7875 7.08699C12.8976 7.2683 13.4435 7.25017 15.5181 6.96007C19.7037 6.39801 21.9239 6.76063 24.1623 8.39244C25.418 9.31713 26.4189 10.6044 27.0559 12.1637C27.438 13.0703 27.5472 13.723 27.62 15.3004C27.7474 17.7844 27.9658 17.0954 28.0022 14.1219C28.0204 11.8374 27.7656 10.4413 27.0013 8.62814C25.236 4.43984 21.6328 1.3938 17.1924 0.324055C15.6455 -0.0385704 12.6246 -0.111094 11.1141 0.179005Z"
            fill="currentColor"
          />
          <path
            d="M10.5857 8.01133C9.03886 8.50087 7.38281 10.8035 7.38281 12.4897C7.38281 14.9556 9.33003 16.8412 11.8414 16.8412C12.8423 16.8412 13.3337 16.5693 12.7149 16.3879C12.078 16.1885 11.2772 15.4089 10.8951 14.5748C10.149 12.9793 10.7131 10.9848 12.1144 10.1689C13.4065 9.42556 14.6985 10.0239 15.663 11.8008C16.2272 12.8524 16.2818 13.0699 16.2818 14.5748C16.2818 16.9319 15.6084 18.2011 13.643 19.6153L13.1881 19.9235L13.9888 19.8147C14.4438 19.7603 15.1171 19.5609 15.5175 19.3977C16.209 19.1076 16.2636 19.1076 17.0643 19.5065C18.0834 20.0142 18.7568 20.0323 19.7577 19.5609C20.7404 19.0714 20.9405 18.6543 20.1762 18.6543C19.5393 18.6543 18.7931 18.3099 18.32 17.8022C18.0106 17.4577 18.0288 17.4033 18.502 16.5693C18.8841 15.8621 19.0115 15.3907 19.0661 14.1941C19.1571 12.526 18.9569 11.692 18.1562 10.5316C16.7549 8.50087 12.9879 7.23169 10.5857 8.01133Z"
            fill="currentColor"
          />
          <path
            d="M0.119091 11.6735C-0.08109 12.8521 -0.0264951 15.3904 0.264678 16.6959C0.574049 18.1645 1.19279 19.76 2.01172 21.1561C2.79424 22.4797 5.61498 25.2719 6.96165 26.0697C9.47302 27.5383 12.9853 28.2817 15.7696 27.9009C18.1172 27.5927 19.2637 26.9944 19.937 25.6889C20.2646 25.0543 20.2646 23.6764 19.937 23.0237C18.8815 21.0111 16.2428 20.4128 11.5112 21.1017C8.92707 21.4825 7.21643 21.2831 5.3602 20.3946C2.26649 18.926 0.66504 16.4239 0.392066 12.6707C0.282876 11.2021 0.228281 11.0571 0.119091 11.6735Z"
            fill="currentColor"
          />
        </svg>
        <span className="text-xl font-bold text-text-primary">
          {tc("brand")}
        </span>
      </div>
    </div>
  );
};
